import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Tabs, Input, Card, Row, Col, Tag, Drawer, Descriptions, Image, Spin, Alert,
  Table, Select, Button, Space, Typography, Empty, message,
  Statistic, Segmented, Collapse, Tooltip,
} from 'antd';
import {
  BugOutlined, SearchOutlined, ReloadOutlined, SyncOutlined,
  EnvironmentOutlined, CommentOutlined, GlobalOutlined,
  RiseOutlined, AreaChartOutlined, AlertOutlined,
} from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { ncpmsAPI } from '../services/api';
import { usePermission } from '../contexts/PermissionContext';
import { brand } from '../theme/tokens';
import { useFitTableHeight } from '../hooks/useViewportFit';
import { fmtDateTimeSec } from '../utils/date';

const { Text, Title, Paragraph } = Typography;

// 병해충 구분코드(divCode) → 태그 색
const DIV_COLORS: Record<string, { label: string; color: string }> = {
  NP01: { label: '병', color: 'red' },
  NP02: { label: '병원체', color: 'volcano' },
  NP03: { label: '해충', color: 'orange' },
  NP04: { label: '곤충', color: 'gold' },
  NP05: { label: '천적곤충', color: 'green' },
  NP06: { label: '잡초', color: 'blue' },
};

// 상세 필드 한글 라벨 (있는 것만 표시, 없으면 원본 키 노출)
const DETAIL_LABELS: Record<string, string> = {
  insectSpeciesKor: '국명', sicknsKorNm: '병명(국문)', weedsKorNm: '잡초명',
  insectSpecies: '학명', sicknsLatinNm: '학명', oprName: '학명',
  cropName: '작물', insectOrder: '목', insectFamily: '과', insectGenus: '속',
  stleInfo: '형태', ecologyInfo: '생태', damageInfo: '피해',
  preventMethod: '방제법', chemicalPrvnbeMth: '화학적 방제',
  biologyPrvnbeMth: '생물적 방제', prvnbeMth: '방제법',
  distrbInfo: '분포', symptomInfo: '병징', sicknsInfo: '병 정보',
  virusNm: '병원체명', dbyhsSpeciesKor: '국명',
};

// 병해충 도감 추천 검색어 (실제 결과가 있는 대표 병해충·작물)
const RECOMMEND_KEYWORDS = [
  '벼멸구', '탄저병', '노균병', '흰가루병', '진딧물', '응애', '총채벌레',
  '역병', '잿빛곰팡이병', '무름병', '벼', '고추', '배추', '사과', '딸기',
];

const IMAGE_KEYS = ['imageList', 'spcsPhotoData', 'photoList', 'sicknsPhotoData', 'weedsPhotoData'];
const HTML_STRIP = (s: string) => (s || '').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();

// ─────────────────────────────────────────────
// 1. 병해충 도감 (검색)
// ─────────────────────────────────────────────
const DogamTab: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailErr, setDetailErr] = useState('');
  const lastKw = useRef('');

  const doSearch = useCallback(async (kw: string, p = 1) => {
    if (!kw.trim()) return;
    setLoading(true); setError(''); setSearched(true); lastKw.current = kw;
    try {
      const res = await ncpmsAPI.search(kw.trim(), p, 20);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail || '검색 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      setItems([]); setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const openDetail = useCallback(async (item: any) => {
    const url = item.detailUrl || '';
    const params = new URLSearchParams(url);
    const serviceCode = params.get('serviceCode') || '';
    // detailUrl 안의 상세 키 파라미터(sickKey/virusKey/insectKey/weedsKey 등) 추출
    // 주의: apiKey 도 'key'로 끝나므로 반드시 제외해야 함
    let key = '';
    params.forEach((v, k) => {
      const lk = k.toLowerCase();
      if (!key && lk !== 'apikey' && lk.endsWith('key')) key = v;
    });
    if (!serviceCode || !key) {
      message.warning('상세 정보를 조회할 수 없는 항목입니다.');
      return;
    }
    setDrawerOpen(true); setDetail(null); setDetailErr(''); setDetailLoading(true);
    try {
      const res = await ncpmsAPI.detail(serviceCode, key);
      setDetail({ ...res.data, __korName: item.korName, __divCode: item.divCode, __cropName: item.cropName });
    } catch (e: any) {
      setDetailErr(e?.response?.data?.detail || '상세 조회 중 오류가 발생했습니다.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const detailImages: string[] = React.useMemo(() => {
    if (!detail) return [];
    const imgs: string[] = [];
    IMAGE_KEYS.forEach((k) => {
      const arr = detail[k];
      if (Array.isArray(arr)) arr.forEach((o: any) => { if (o?.image) imgs.push(o.image); });
    });
    return imgs;
  }, [detail]);

  const detailRows: [string, string][] = React.useMemo(() => {
    if (!detail) return [];
    const rows: [string, string][] = [];
    Object.entries(detail).forEach(([k, v]) => {
      if (k.startsWith('__') || Array.isArray(v) || typeof v !== 'string') return;
      const val = HTML_STRIP(v as string);
      if (val) rows.push([DETAIL_LABELS[k] || k, val]);
    });
    return rows;
  }, [detail]);

  const runRecommend = useCallback((w: string) => { setKeyword(w); doSearch(w, 1); }, [doSearch]);

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <Input.Search
          placeholder="작물명 또는 병해충명 검색 (예: 벼멸구, 탄저병, 잡초)"
          enterButton={<><SearchOutlined /> 검색</>}
          size="large"
          allowClear
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onSearch={(v) => doSearch(v, 1)}
          loading={loading}
          style={{ width: 460, maxWidth: '100%' }}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>추천어</Text>
          {RECOMMEND_KEYWORDS.map((w) => (
            <Tag key={w} color="green" style={{ cursor: 'pointer', margin: 0 }} onClick={() => runRecommend(w)}>{w}</Tag>
          ))}
        </div>
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }}
        action={<Button size="small" onClick={() => doSearch(lastKw.current, page)}>재시도</Button>} />}
      <Spin spinning={loading}>
        {items.length > 0 ? (
          <>
            <Row gutter={[16, 16]}>
              {items.map((it, idx) => {
                const dv = DIV_COLORS[it.divCode] || { label: it.divName || '', color: 'default' };
                return (
                  <Col xs={12} sm={8} md={6} lg={4} key={`${it.detailUrl || idx}`}>
                    <Card hoverable size="small" onClick={() => openDetail(it)}
                      cover={<div style={{ height: 120, overflow: 'hidden', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img alt={it.korName} src={it.thumbImg}
                          style={{ maxHeight: 120, maxWidth: '100%', objectFit: 'cover' }}
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      </div>}
                      styles={{ body: { padding: 10 } }}>
                      <Tag color={dv.color} style={{ marginBottom: 4 }}>{dv.label}</Tag>
                      <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>{it.korName}</div>
                      <Text type="secondary" style={{ fontSize: 11 }}>{it.cropName}</Text>
                    </Card>
                  </Col>
                );
              })}
            </Row>
            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Space>
                <Button disabled={page <= 1} onClick={() => doSearch(lastKw.current, page - 1)}>이전</Button>
                <Text>{page} / {Math.max(1, Math.ceil(total / 20))} (총 {total}건)</Text>
                <Button disabled={page >= Math.ceil(total / 20)} onClick={() => doSearch(lastKw.current, page + 1)}>다음</Button>
              </Space>
            </div>
          </>
        ) : (searched && !loading && !error && <Empty description="검색 결과가 없습니다." />)}
        {!searched && <Empty image={<BugOutlined style={{ fontSize: 48, color: brand.primary }} />}
          description="작물명이나 병해충명을 검색하세요." />}
      </Spin>

      <Drawer title={detail ? `${detail.__korName || '상세 정보'}` : '상세 정보'}
        width={720} open={drawerOpen} onClose={() => setDrawerOpen(false)} destroyOnClose>
        {detailLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>}
        {detailErr && <Alert type="error" showIcon message={detailErr} />}
        {detail && !detailLoading && (
          <>
            {detailImages.length > 0 && (
              <Image.PreviewGroup>
                <Space wrap style={{ marginBottom: 16 }}>
                  {detailImages.slice(0, 12).map((src, i) => (
                    <Image key={i} src={src} width={100} height={100} style={{ objectFit: 'cover' }} />
                  ))}
                </Space>
              </Image.PreviewGroup>
            )}
            <Descriptions column={1} bordered size="small">
              {detailRows.map(([label, val]) => (
                <Descriptions.Item key={label} label={label}>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{val}</div>
                </Descriptions.Item>
              ))}
            </Descriptions>
          </>
        )}
      </Drawer>
    </div>
  );
};

// ─────────────────────────────────────────────
// 2. 예찰정보 — 전문가 발생 분석 대시보드
// ─────────────────────────────────────────────
// 측정단위별 색 (트랩/밀도=곤충 계열 주황, 발생/피해=병 계열 빨강, 면적=파랑)
const UNIT_TONE = (unit: string): string => {
  if (/마리|밀도|트랩|난괴|약충|성충/.test(unit)) return '#f97316';   // 해충 밀도
  if (/면적/.test(unit)) return '#0ea5e9';                            // 발생면적
  if (/율|률|감수|피해|병든|발병|발생주/.test(unit)) return '#ef4444'; // 피해/발병율
  return brand.primary;
};
const fmtDate = (d: string) => d && d.length === 8 ? `${d.slice(4, 6)}/${d.slice(6, 8)}` : d;
const fmtNum = (v: number | null) => v == null ? '-' : (Math.abs(v) >= 100 ? Math.round(v).toLocaleString() : v.toFixed(v % 1 === 0 ? 0 : 2));

const Sparkline: React.FC<{ data: (number | null)[]; color: string }> = ({ data, color }) => {
  const clean = data.map((v) => (v == null ? 0 : v));
  return (
    <ReactECharts style={{ height: 28, width: 90 }} opts={{ renderer: 'svg' }} option={{
      grid: { left: 1, right: 1, top: 3, bottom: 3 },
      xAxis: { type: 'category', show: false, data: clean.map((_, i) => i) },
      yAxis: { type: 'value', show: false },
      series: [{ type: 'line', data: clean, showSymbol: false, smooth: true,
        lineStyle: { width: 1.5, color }, areaStyle: { color, opacity: 0.12 } }],
    }} />
  );
};

const SurveyDashboard: React.FC<{ canSync: boolean }> = ({ canSync }) => {
  const [filters, setFilters] = useState<{ years: string[]; crops: string[]; kinds: string[] }>({ years: [], crops: [], kinds: [] });
  const [crop, setCrop] = useState<string>();
  const [year, setYear] = useState<string>();
  const [kind, setKind] = useState<string>();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selPest, setSelPest] = useState<string>('');   // label
  const [trendMode, setTrendMode] = useState<'national' | 'sido'>('national');
  const [lastSync, setLastSync] = useState<string>('');
  const [syncing, setSyncing] = useState(false);
  const [pestQuery, setPestQuery] = useState('');

  const loadAnalysis = useCallback(async (c?: string, y?: string, k?: string) => {
    if (!c) return;
    setLoading(true);
    try {
      const res = await ncpmsAPI.surveyAnalysis({ crop: c, year: y, survey_kind: k });
      setData(res.data);
      setYear(res.data.year);
      setKind(res.data.survey_kind);
      const first = (res.data.pests || [])[0];
      setSelPest(first ? first.label : '');
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '예찰 분석 조회 실패');
      setData(null);
    } finally { setLoading(false); }
  }, []);

  // 필터 옵션 + 마지막 수집시각 로드, 첫 작물 자동 선택
  useEffect(() => {
    ncpmsAPI.surveyFilters().then((r) => {
      setFilters(r.data);
      const c = (r.data.crops || [])[0];
      if (c) { setCrop(c); loadAnalysis(c); }
    }).catch(() => {});
    ncpmsAPI.surveys({ page_size: 1 }).then((r) => { if (r.data.last_sync) setLastSync(r.data.last_sync); }).catch(() => {});
  }, [loadAnalysis]);

  const doSync = useCallback(async () => {
    setSyncing(true);
    try {
      const res = await ncpmsAPI.sync();
      message.success(res.data.message || '동기화 완료');
      loadAnalysis(crop, year, kind);
      ncpmsAPI.surveys({ page_size: 1 }).then((r) => { if (r.data.last_sync) setLastSync(r.data.last_sync); });
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '동기화 실패');
    } finally { setSyncing(false); }
  }, [crop, year, kind, loadAnalysis]);

  const rounds: any[] = data?.rounds || [];
  const roundLabels = rounds.map((r) => `${r.round}회\n${fmtDate(r.date)}`);
  const pests: any[] = data?.pests || [];
  const pest = pests.find((p) => p.label === selPest) || pests[0];

  const filteredPests = React.useMemo(() => {
    const q = pestQuery.trim();
    return q ? pests.filter((p) => p.label.includes(q)) : pests;
  }, [pests, pestQuery]);

  // 발생 추세 라인차트 옵션
  const trendOption = React.useMemo(() => {
    if (!pest) return null;
    const tone = UNIT_TONE(pest.unit);
    if (trendMode === 'national') {
      return {
        grid: { left: 56, right: 20, top: 24, bottom: 50 },
        tooltip: { trigger: 'axis' },
        xAxis: { type: 'category', data: roundLabels, axisLabel: { fontSize: 10, lineHeight: 13 } },
        yAxis: { type: 'value', name: pest.unit },
        series: [{
          name: '전국 평균', type: 'line', data: pest.national, smooth: true,
          connectNulls: true, showSymbol: true, symbolSize: 6,
          lineStyle: { width: 2.5, color: tone }, itemStyle: { color: tone },
          areaStyle: { color: tone, opacity: 0.1 },
          markPoint: { data: [{ type: 'max', name: '최고' }], symbolSize: 44 },
        }],
      };
    }
    const sidoSeries = pest.sido_series || {};
    return {
      grid: { left: 56, right: 20, top: 24, bottom: 50 },
      tooltip: { trigger: 'axis' },
      legend: { type: 'scroll', bottom: 0, textStyle: { fontSize: 10 } },
      xAxis: { type: 'category', data: roundLabels, axisLabel: { fontSize: 10, lineHeight: 13 } },
      yAxis: { type: 'value', name: pest.unit },
      series: Object.entries(sidoSeries).map(([sido, vals]) => ({
        name: sido, type: 'line', data: vals as any[], smooth: true, connectNulls: true,
        showSymbol: false, lineStyle: { width: 1.5 },
      })),
    };
  }, [pest, trendMode, roundLabels]);

  // 최신 회차 시도별 현황 (내림차순)
  const sidoBarOption = React.useMemo(() => {
    if (!pest) return null;
    const entries = Object.entries(pest.latest_by_sido || {})
      .map(([k, v]) => ({ k, v: v as number })).filter((e) => e.v > 0)
      .sort((a, b) => a.v - b.v);
    const tone = UNIT_TONE(pest.unit);
    return {
      grid: { left: 90, right: 30, top: 10, bottom: 24 },
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: entries.map((e) => e.k), axisLabel: { fontSize: 11 } },
      series: [{ type: 'bar', data: entries.map((e) => e.v), itemStyle: { color: tone, borderRadius: [0, 3, 3, 0] },
        barMaxWidth: 18, label: { show: true, position: 'right', fontSize: 10, formatter: (p: any) => fmtNum(p.value) } }],
    };
  }, [pest]);

  // 원자료: 선택 병해충의 회차×시도 매트릭스
  const matrixColumns = React.useMemo(() => {
    if (!pest) return [];
    return [
      { title: '시도', dataIndex: 'sido', fixed: 'left' as const, width: 120 },
      ...rounds.map((r, i) => ({
        title: `${r.round}회 (${fmtDate(r.date)})`, dataIndex: `r${i}`, width: 92, align: 'right' as const,
        render: (v: number | null) => v == null ? <Text type="secondary">-</Text>
          : v > 0 ? <Text strong style={{ color: UNIT_TONE(pest.unit) }}>{fmtNum(v)}</Text> : <Text type="secondary">0</Text>,
      })),
    ];
  }, [pest, rounds]);

  const matrixData = React.useMemo(() => {
    if (!pest) return [];
    const ss = pest.sido_series || {};
    return Object.entries(ss).map(([sido, vals]) => {
      const row: any = { key: sido, sido };
      (vals as any[]).forEach((v, i) => { row[`r${i}`] = v; });
      return row;
    });
  }, [pest]);

  const cropOptions = (filters.crops || []).map((c) => ({ label: c, value: c }));
  const yearOptions = (filters.years || []).map((y) => ({ label: y, value: y }));
  const kindOptions = (data?.kinds || []).filter(Boolean).map((k: string) => ({ label: k, value: k }));

  return (
    <div>
      {/* 컨트롤 바 */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Select showSearch placeholder="작물 선택" style={{ width: 150 }} value={crop}
          onChange={(c) => { setCrop(c); loadAnalysis(c); }} options={cropOptions} />
        <Select placeholder="연도" style={{ width: 100 }} value={year}
          onChange={(y) => { setYear(y); loadAnalysis(crop, y); }} options={yearOptions} />
        {kindOptions.length > 1 && (
          <Select placeholder="조사구분" style={{ width: 140 }} value={kind}
            onChange={(k) => { setKind(k); loadAnalysis(crop, year, k); }} options={kindOptions} />
        )}
        <Button icon={<ReloadOutlined />} onClick={() => loadAnalysis(crop, year, kind)}>재조회</Button>
        {canSync && <Button type="primary" icon={<SyncOutlined spin={syncing} />} loading={syncing} onClick={doSync}>지금 동기화</Button>}
        {lastSync && <Text type="secondary">최근 수집: {fmtDateTimeSec(lastSync)}</Text>}
      </Space>

      <Spin spinning={loading}>
        {!data || pests.length === 0 ? (
          <Empty description={loading ? ' ' : '분석할 예찰 데이터가 없습니다. 작물·연도를 선택하세요.'} />
        ) : (
          <>
            {/* 요약 KPI */}
            <Row gutter={12} style={{ marginBottom: 16 }}>
              <Col xs={12} md={6}><Card size="small"><Statistic title="조사 회차" value={rounds.length} suffix="회"
                prefix={<AreaChartOutlined style={{ color: brand.primary }} />} /></Card></Col>
              <Col xs={12} md={6}><Card size="small"><Statistic title="발생 병해충" value={data.active_count}
                suffix={`/ ${data.total_pests}종`} prefix={<AlertOutlined style={{ color: '#ef4444' }} />} /></Card></Col>
              <Col xs={12} md={6}><Card size="small"><Statistic title="최신 조사"
                value={rounds.length ? `${rounds[rounds.length - 1].round}회` : '-'}
                suffix={rounds.length ? fmtDate(rounds[rounds.length - 1].date) : ''} /></Card></Col>
              <Col xs={12} md={6}><Card size="small">
                <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>최다 발생</div>
                <div style={{ fontSize: 15, fontWeight: 700, lineHeight: 1.2 }}>{pests[0]?.name}</div>
                <Text type="secondary" style={{ fontSize: 11 }}>{pests[0]?.unit}</Text>
              </Card></Col>
            </Row>

            <Row gutter={16}>
              {/* 좌: 병해충 발생 순위 */}
              <Col xs={24} lg={9}>
                <Card size="small" title={<span><RiseOutlined /> 주요 병해충 발생 순위</span>}
                  styles={{ body: { padding: 0 } }}
                  extra={<Input.Search placeholder="병해충 검색" size="small" allowClear
                    style={{ width: 130 }} onChange={(e) => setPestQuery(e.target.value)} />}>
                  <div style={{ maxHeight: 520, overflowY: 'auto' }}>
                    {filteredPests.map((p) => {
                      const tone = UNIT_TONE(p.unit);
                      const on = p.label === selPest;
                      return (
                        <div key={p.label} onClick={() => setSelPest(p.label)}
                          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer',
                            borderLeft: `3px solid ${on ? tone : 'transparent'}`, background: on ? `${tone}10` : undefined,
                            borderBottom: '1px solid #f0f0f0' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: on ? 700 : 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {p.name}
                            </div>
                            <Text type="secondary" style={{ fontSize: 10 }}>{p.unit}</Text>
                          </div>
                          <Sparkline data={p.national} color={tone} />
                          <div style={{ textAlign: 'right', minWidth: 58 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: p.latest > 0 ? tone : '#bbb' }}>{fmtNum(p.latest)}</div>
                            <Text type="secondary" style={{ fontSize: 9 }}>최고 {fmtNum(p.peak)}</Text>
                          </div>
                        </div>
                      );
                    })}
                    {filteredPests.length === 0 && <Empty style={{ padding: 24 }} description="일치하는 병해충 없음" />}
                  </div>
                </Card>
              </Col>

              {/* 우: 추세 + 시도별 */}
              <Col xs={24} lg={15}>
                {pest && (
                  <Card size="small" style={{ marginBottom: 16 }}
                    title={<span><AreaChartOutlined /> {pest.name} <Text type="secondary" style={{ fontSize: 12 }}>({pest.unit})</Text> 회차별 발생 추세</span>}
                    extra={<Segmented size="small" value={trendMode} onChange={(v) => setTrendMode(v as any)}
                      options={[{ label: '전국평균', value: 'national' }, { label: '시도별', value: 'sido' }]} />}>
                    {trendOption ? <ReactECharts style={{ height: 300 }} option={trendOption} notMerge /> : <Empty />}
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      * 회차가 진행될수록 값이 상승하면 발생이 확산 중임을 의미합니다. 방제 시점 판단에 활용하세요.
                    </Text>
                  </Card>
                )}
                {pest && (
                  <Card size="small" title={<span><EnvironmentOutlined /> 최신 회차({pest.latest_round}회) 시도별 현황</span>}>
                    {sidoBarOption && (Object.keys(pest.latest_by_sido || {}).some((k) => pest.latest_by_sido[k] > 0))
                      ? <ReactECharts style={{ height: 260 }} option={sidoBarOption} notMerge />
                      : <Empty description="최신 회차에 발생(값>0) 지역이 없습니다." />}
                  </Card>
                )}
              </Col>
            </Row>

            {/* 원자료: 회차×시도 매트릭스 */}
            {pest && (
              <Collapse ghost style={{ marginTop: 8 }} items={[{
                key: 'raw',
                label: <span><Tooltip title="선택한 병해충의 회차별·시도별 원측정값">원자료 보기 — {pest.label} 회차×시도 매트릭스</Tooltip></span>,
                children: (
                  <Table size="small" bordered scroll={{ x: 'max-content' }} pagination={false}
                    columns={matrixColumns as any} dataSource={matrixData} />
                ),
              }]} />
            )}
          </>
        )}
      </Spin>
    </div>
  );
};

// ── 예찰정보 - 목록 (표 형태) ──
const SurveyList: React.FC = () => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{ years: string[]; crops: string[]; kinds: string[] }>({ years: [], crops: [], kinds: [] });
  const [year, setYear] = useState<string>();
  const [crop, setCrop] = useState<string>();
  const [kind, setKind] = useState<string>();
  const [lastSync, setLastSync] = useState<string>('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [struct, setStruct] = useState<any[]>([]);
  const [structLoading, setStructLoading] = useState(false);
  const [structPest, setStructPest] = useState<string>('');

  const load = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await ncpmsAPI.surveys({ year, crop, kind, page: p, page_size: pageSize });
      setRows(res.data.items || []);
      setTotal(res.data.total || 0);
      setPage(p);
      if (res.data.last_sync) setLastSync(res.data.last_sync);
    } catch { message.error('예찰 목록을 불러오지 못했습니다.'); } finally { setLoading(false); }
  }, [year, crop, kind, pageSize]);

  useEffect(() => { ncpmsAPI.surveyFilters().then((r) => setFilters(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(1); }, [load]);

  const openDetail = useCallback(async (row: any) => {
    setDetail(row); setDetailOpen(true); setStruct([]); setStructPest(''); setStructLoading(true);
    try {
      const res = await ncpmsAPI.surveyDetail(row.insect_key);
      const sl = res.data.struct_list || [];
      setStruct(sl);
      if (sl.length) setStructPest(sl[0].dbyhsNm);
    } catch { message.error('예찰 상세를 불러오지 못했습니다.'); } finally { setStructLoading(false); }
  }, []);

  const pestOptions = React.useMemo(
    () => Array.from(new Set(struct.map((s) => s.dbyhsNm))).map((v) => ({ label: v, value: v })), [struct]);

  const columns = [
    { title: '작물', dataIndex: 'kncr_nm', width: 100 },
    { title: '년도', dataIndex: 'examin_year', width: 70 },
    { title: '회차', dataIndex: 'examin_tmrd', width: 60, align: 'center' as const },
    { title: '조사종류', dataIndex: 'examin_spchckn_nm', width: 120 },
    { title: '예찰종류', dataIndex: 'predictn_spchckn_nm' },
    { title: '기준일', dataIndex: 'input_stdr_date', width: 110,
      render: (v: string) => v && v.length === 8 ? `${v.slice(0, 4)}-${v.slice(4, 6)}-${v.slice(6, 8)}` : v },
  ];

  return (
    <div ref={cardWrapRef}>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select allowClear placeholder="년도" style={{ width: 110 }} value={year} onChange={setYear}
          options={(filters.years || []).map((y) => ({ label: y, value: y }))} />
        <Select allowClear showSearch placeholder="작물" style={{ width: 150 }} value={crop} onChange={setCrop}
          options={(filters.crops || []).map((c) => ({ label: c, value: c }))} />
        <Select allowClear placeholder="조사종류" style={{ width: 140 }} value={kind} onChange={setKind}
          options={(filters.kinds || []).map((k) => ({ label: k, value: k }))} />
        <Button icon={<ReloadOutlined />} onClick={() => load(1)}>재조회</Button>
        {lastSync && <Text type="secondary">최근 수집: {fmtDateTimeSec(lastSync)}</Text>}
      </Space>

      <div ref={tableWrapRef}>
      <Table rowKey="insect_key" size="small" loading={loading} columns={columns} dataSource={rows}
        onRow={(r) => ({ onClick: () => openDetail(r), style: { cursor: 'pointer' } })}
        scroll={{ y: bodyHeight }}
        pagination={{ current: page, total, pageSize,
          showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (p, ps) => { if (ps !== pageSize) setPageSize(ps); else load(p); },
          showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건` }} />
      </div>

      <Drawer width={720} destroyOnClose open={detailOpen} onClose={() => setDetailOpen(false)}
        title={detail ? `${detail.kncr_nm} ${detail.examin_year}년 ${detail.examin_tmrd}회차 — 시도별 예찰값` : '예찰 상세'}>
        <Spin spinning={structLoading}>
          {struct.length > 0 ? (
            <>
              <Space style={{ marginBottom: 12 }}>
                <Text>조사항목:</Text>
                <Select style={{ minWidth: 260 }} value={structPest} onChange={setStructPest} options={pestOptions} />
              </Space>
              <Table size="small" pagination={false}
                rowKey={(r: any, i) => `${r.sidoCode}-${r.inqireCnClCode}-${i}`}
                dataSource={struct.filter((s) => s.dbyhsNm === structPest)}
                columns={[
                  { title: '병해충/항목', dataIndex: 'dbyhsNm' },
                  { title: '시도', dataIndex: 'sidoNm', width: 140 },
                  { title: '값', dataIndex: 'inqireValue', width: 100, align: 'right' as const,
                    render: (v: number) => fmtNum(v) },
                ]} />
            </>
          ) : (!structLoading && <Empty description="시도별 예찰 데이터가 없습니다." />)}
        </Spin>
      </Drawer>
    </div>
  );
};

// ── 예찰정보 래퍼 (대시보드 / 목록 서브탭) ──
const SurveyTab: React.FC<{ canSync: boolean }> = ({ canSync }) => (
  <Tabs type="card" size="small" items={[
    { key: 'dash', label: '예찰정보-대시보드', children: <SurveyDashboard canSync={canSync} /> },
    { key: 'list', label: '예찰정보-목록', children: <SurveyList /> },
  ]} />
);

// ─────────────────────────────────────────────
// 3. 병해충 상담 Q&A
// ─────────────────────────────────────────────
const ConsultTab: React.FC = () => {
  const { tableWrapRef, cardWrapRef, bodyHeight } = useFitTableHeight();
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const load = useCallback(async (p = 1) => {
    setLoading(true); setError('');
    try {
      const res = await ncpmsAPI.consults(p, pageSize);
      setRows(res.data.items || []); setTotal(res.data.total || 0); setPage(p);
    } catch (e: any) {
      setError(e?.response?.data?.detail || '상담 목록 조회 중 오류가 발생했습니다.');
    } finally { setLoading(false); }
  }, [pageSize]);

  useEffect(() => { load(1); }, [load]);

  const openDetail = useCallback(async (row: any) => {
    setOpen(true); setDetail(null); setDetailLoading(true);
    try {
      const res = await ncpmsAPI.consultDetail(row.dgnssReqNo);
      setDetail(res.data);
    } catch { message.error('상담 상세를 불러오지 못했습니다.'); } finally { setDetailLoading(false); }
  }, []);

  const columns = [
    { title: '번호', dataIndex: 'dgnssReqNo', width: 80 },
    { title: '제목', dataIndex: 'dgnssReqSj' },
    { title: '작물', dataIndex: 'cropName', width: 120 },
    { title: '분야', dataIndex: 'realm', width: 100, render: (v: string) => v ? <Tag>{v}</Tag> : null },
    { title: '등록일', dataIndex: 'registDatetm', width: 110 },
  ];

  return (
    <div ref={cardWrapRef}>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }}
        action={<Button size="small" onClick={() => load(page)}>재시도</Button>} />}
      <div ref={tableWrapRef}>
      <Table rowKey="dgnssReqNo" size="small" loading={loading} columns={columns} dataSource={rows}
        onRow={(r) => ({ onClick: () => openDetail(r), style: { cursor: 'pointer' } })}
        scroll={{ y: bodyHeight }}
        pagination={{ current: page, total, pageSize,
          showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (p, ps) => { if (ps !== pageSize) setPageSize(ps); else load(p); },
          showTotal: (t, r) => `${r[0]}–${r[1]} / 총 ${t}건` }} />
      </div>

      <Drawer title="병해충 상담 상세" width={720} open={open} onClose={() => setOpen(false)} destroyOnClose>
        {detailLoading && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>}
        {detail && !detailLoading && (
          <>
            <Title level={5}>{detail.dgnssReqSj}</Title>
            <Space wrap style={{ marginBottom: 12 }}>
              {detail.cropName && <Tag color="green">{detail.cropName}</Tag>}
              {detail.realm && <Tag color="blue">{detail.realm}</Tag>}
              {detail.sidoName && <Tag>{detail.sidoName} {detail.sigunguName || ''}</Tag>}
              {detail.occrrncDatetm && <Text type="secondary">발생: {detail.occrrncDatetm}</Text>}
            </Space>
            <Card size="small" title="질문" style={{ marginBottom: 16 }}>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{HTML_STRIP(detail.reqestCn)}</Paragraph>
            </Card>
            <Card size="small" title={`답변${detail.answrrName ? ` — ${detail.answrrName}` : ''}${detail.answerDatetm ? ` (${detail.answerDatetm})` : ''}`}>
              <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{HTML_STRIP(detail.dgnssOpin) || '답변 준비 중'}</Paragraph>
              {detail.prvnbeMth && (
                <>
                  <Text strong>방제 방법</Text>
                  <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{HTML_STRIP(detail.prvnbeMth)}</Paragraph>
                </>
              )}
            </Card>
          </>
        )}
      </Drawer>
    </div>
  );
};

// ─────────────────────────────────────────────
// 4. 예측 포털 (NCPMS 예측지도 딥링크 + 예찰기반 발생 급증 인사이트)
// ─────────────────────────────────────────────
// NCPMS 예측지도 제공 작물 (SVC31 기준)
const FORECAST_CROPS = [
  { name: '논벼', code: 'FC010101' }, { name: '고추', code: 'VC011205' },
  { name: '사과', code: 'FT010601' }, { name: '배', code: 'FT010602' },
  { name: '포도', code: 'FT040603' }, { name: '감귤', code: 'FT060614' },
  { name: '감자', code: 'FC050501' }, { name: '마늘', code: 'VC041209' },
  { name: '파', code: 'VC041202' }, { name: '콩', code: 'FC030301' },
];
const NCPMS_FORECAST_URL = 'https://ncpms.rda.go.kr/npms/NewSpaceDistributionSearchR.np';
const openForecast = (code?: string) =>
  window.open(code ? `${NCPMS_FORECAST_URL}?kncrCode=${code}` : NCPMS_FORECAST_URL, '_blank');

// 예찰 시계열로 발생 추세 판정
const surveyTrend = (national: (number | null)[]): { tag: string; color: string; rank: number } => {
  const xs = national.filter((v): v is number => v != null);
  if (xs.length < 2 || xs[xs.length - 1] === 0) return { tag: '─', color: '#bbb', rank: 0 };
  const latest = xs[xs.length - 1];
  const prev = [...xs.slice(0, -1)].reverse().find((v) => v > 0) ?? 0;
  if (latest > prev * 1.2) return { tag: '▲ 급증', color: '#dc2626', rank: 3 };
  if (latest > prev) return { tag: '▲ 증가', color: '#f97316', rank: 2 };
  if (latest < prev * 0.8) return { tag: '▼ 감소', color: '#0ea5e9', rank: -1 };
  return { tag: '─ 유지', color: '#64748b', rank: 1 };
};

const ForecastPortalTab: React.FC = () => {
  const [crop, setCrop] = useState<string>('논벼');
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (c: string) => {
    setLoading(true);
    try {
      const res = await ncpmsAPI.surveyAnalysis({ crop: c });
      setData(res.data);
    } catch { setData(null); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(crop); }, [load, crop]);

  // 발생 중(latest>0)인 병해충을 추세 순위 → 최신값 순으로 정렬
  const rising = React.useMemo(() => {
    const pests = (data?.pests || []).filter((p: any) => p.latest > 0);
    return pests.map((p: any) => ({ ...p, __t: surveyTrend(p.national) }))
      .sort((a: any, b: any) => (b.__t.rank - a.__t.rank) || (b.latest - a.latest))
      .slice(0, 10);
  }, [data]);

  return (
    <div>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="병해충 예측지도 안내"
        description={<span>
          농촌진흥청 예측지도는 <b>GIS 기반 화면 서비스</b>로만 제공되어(데이터 연동 미승인) SAPPT 내장 표시가 불가합니다.
          <b>작물을 선택</b>하면 아래 <b>발생 추세</b>가 갱신되고, <b>“예측지도 열기”</b> 버튼으로 해당 작물의 NCPMS 공식 예측지도를 새 창에서 볼 수 있습니다.
        </span>} />

      <Card size="small" style={{ marginBottom: 16 }}
        title={<span><EnvironmentOutlined /> 작물 선택</span>}
        extra={<Space>
          <Button type="primary" icon={<GlobalOutlined />}
            onClick={() => openForecast(FORECAST_CROPS.find((c) => c.name === crop)?.code)}>
            {crop} 예측지도 열기
          </Button>
          <Button size="small" onClick={() => openForecast()}>전체</Button>
        </Space>}>
        <Space wrap>
          {FORECAST_CROPS.map((c) => (
            <Button key={c.code} onClick={() => setCrop(c.name)}
              type={c.name === crop ? 'primary' : 'default'}>{c.name}</Button>
          ))}
        </Space>
      </Card>

      <Card size="small"
        title={<span><RiseOutlined /> 예찰 기반 발생 추세 — <Text strong>{crop}</Text> (예측 참고자료)</span>}
        extra={<Button size="small" icon={<GlobalOutlined />}
          onClick={() => openForecast(FORECAST_CROPS.find((c) => c.name === crop)?.code)}>
          {crop} 예측지도 열기</Button>}>
        <Spin spinning={loading}>
          {rising.length > 0 ? (
            <>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {data?.year}년 {data?.survey_kind} 기준 · 최신 회차에 발생 중인 병해충을 추세순으로 표시합니다. <b style={{ color: '#dc2626' }}>▲급증</b> 항목은 방제 대비가 필요합니다.
              </Text>
              <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
                {rising.map((p: any) => {
                  const tone = UNIT_TONE(p.unit);
                  return (
                    <Col xs={24} sm={12} lg={8} key={p.label}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                        border: '1px solid #f0f0f0', borderRadius: 8, borderLeft: `3px solid ${tone}` }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                          <Text type="secondary" style={{ fontSize: 10 }}>{p.unit}</Text>
                        </div>
                        <Sparkline data={p.national} color={tone} />
                        <div style={{ textAlign: 'right', minWidth: 60 }}>
                          <Tag color={p.__t.color === '#bbb' ? 'default' : undefined}
                            style={{ margin: 0, color: p.__t.color, borderColor: p.__t.color, background: 'transparent', fontWeight: 700 }}>
                            {p.__t.tag}
                          </Tag>
                          <div style={{ fontSize: 12, fontWeight: 700, color: tone, marginTop: 2 }}>{fmtNum(p.latest)}</div>
                        </div>
                      </div>
                    </Col>
                  );
                })}
              </Row>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 12 }}>
                * 상세 회차별 추세·시도별 현황은 <b>예찰정보</b> 탭에서 확인하세요.
              </Text>
            </>
          ) : (!loading && <Empty description={`${crop}의 최신 조사에서 발생(값>0) 병해충이 없습니다.`} />)}
        </Spin>
      </Card>
    </div>
  );
};

// ─────────────────────────────────────────────
const NcpmsPage: React.FC = () => {
  const { hasPermission } = usePermission();
  const canSync = hasPermission('ncpms', 'WRITE');
  const [activeTab, setActiveTab] = useState('dogam');

  return (
    /* 하단 패딩은 실측(cardWrapRef) 밖이라 세로스크롤을 만든다 — Content 20px 패딩이 하단 여백 역할 */
    <div style={{ padding: '24px 24px 0' }}>
      <Title level={4} style={{ marginBottom: 4 }}>
        <BugOutlined style={{ color: brand.primary, marginRight: 8 }} />병해충 정보 (NCPMS)
      </Title>
      <Text type="secondary">농촌진흥청 국가농작물병해충관리시스템 연동 — 병해충 검색·예찰·상담·예측</Text>
      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ marginTop: 16 }} items={[
        { key: 'dogam', label: <span><SearchOutlined /> 병해충 도감</span>, children: <DogamTab /> },
        { key: 'survey', label: <span><EnvironmentOutlined /> 예찰정보</span>, children: <SurveyTab canSync={canSync} /> },
        { key: 'consult', label: <span><CommentOutlined /> 상담 Q&A</span>, children: <ConsultTab /> },
        { key: 'map', label: <span><GlobalOutlined /> 예측</span>, children: <ForecastPortalTab /> },
      ]} />
    </div>
  );
};

export default NcpmsPage;
